import React, { useEffect, useMemo, useState } from "react";
import StudentLeftside from "./StudentLeftside";
import ActivePlans from "./ActivePlans";
import LastQuizAttempts from "./LastQuizAttempts";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import "./studentdashboard.css";
import API_BASE_URL from "../config/api";

const DASHBOARD_ENDPOINT = `${API_BASE_URL}/dashboard_summary.php`;

/* ================= DONUT STAT CARD ================= */
function DashboardStatCard({
  value,
  label,
  subText,
  percent = 0,
  icon,
  theme,
}) {
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className={`stat-card ${theme}`}>
      <div className="stat-left">
        <div className="stat-icon">
          <i className={icon}></i>
        </div>

        <div>
          <h4>{value}</h4>
          <p>{label}</p>
          <small>{subText}</small>
        </div>
      </div>

      <div className="stat-right">
        <svg width="56" height="56">
          <circle
            cx="28"
            cy="28"
            r={radius}
            stroke="#e9ecef"
            strokeWidth="6"
            fill="none"
          />

          <circle
            cx="28"
            cy="28"
            r={radius}
            stroke="currentColor"
            strokeWidth="6"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>

        <span className="stat-percent">{percent}%</span>
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ DASHBOARD COUNTS
  const [masterExamAttempt, setMasterExamAttempt] = useState(0);
  const [chapterExamAttempt, setChapterExamAttempt] = useState(0);
  const [streakCount, setStreakCount] = useState(0);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 991
  );

  /* ================= FETCH DASHBOARD ================= */
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));

    if (!user?.id) {
      Swal.fire(
        "Not Logged In",
        "Please login again",
        "warning"
      ).then(() => {
        window.location.href = "/login";
      });

      return;
    }

    fetch(`${DASHBOARD_ENDPOINT}?user_id=${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        console.log("Dashboard API Response:", data);
        if (!data.success) {
          throw new Error("API failed");
        }

        // ✅ API DATA
        setMasterExamAttempt(
          Number(data.master_exam_attempt) || 0
        );

        setChapterExamAttempt(
          Number(data.chapter_exam_attempt) || 0
        );

        setStreakCount(
          Number(data.streak_count) || 0
        );

        setLoading(false);
      })
      .catch((err) => {
        console.error(err);

        setError("Failed to load dashboard");
        setLoading(false);
      });
  }, []);

  /* ================= MOBILE CHECK ================= */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    window.addEventListener("resize", handleResize);

    return () =>
      window.removeEventListener("resize", handleResize);
  }, []);

  /* ================= STATS ================= */
  const stats = useMemo(() => {
    return {
      masterAttemptsCount: masterExamAttempt,
      chapterAttemptsCount: chapterExamAttempt,
      streakCount: streakCount,

      masterPercent:
        masterExamAttempt > 0 ? 100 : 0,

      chapterPercent:
        chapterExamAttempt > 0 ? 100 : 0,

      streakPercent:
        streakCount > 0 ? 0 : 0,
    };
  }, [
    masterExamAttempt,
    chapterExamAttempt,
    streakCount,
  ]);

  if (loading) {
    return <div className="p-5">Loading...</div>;
  }

  return (
    <>
      <div className="dashboard-wrap bg-light">
        {!isMobile && <StudentLeftside />}

        <div className="dashboard-content container-fluid pt-8">

          {/* ===== HEADER ===== */}
          <div className="dashboard-tlbar d-block pt-5">
            <div className="row">
              <div className="col-12">
                <h3 className="float-lg-start">
                  Dashboard
                </h3>
              </div>
            </div>
          </div>

          {/* ===== ACTIVE PLANS ===== */}
          <ActivePlans />

          {/* ===== ERROR ===== */}
          {error && (
            <div className="alert alert-warning">
              {error}
            </div>
          )}


          {/* ===== STATS ===== */}
          <div className="row g-3 mb-4">

            {/* MASTER QUIZ */}
            <div className="col-12 col-md-4">
              <Link
                to="/masterquizlist"
                style={{ textDecoration: "none" }}
              >
                <DashboardStatCard
                  value={stats.masterAttemptsCount}
                  label="Master Quizzes"
                  subText="Attempted"
                  percent={stats.masterPercent}
                  icon="fa-solid fa-layer-group"
                  theme="stat-orange"
                />
              </Link>
            </div>

            {/* CHAPTER QUIZ */}
            <div className="col-12 col-md-4">
              <Link
                to="/chapterwisequizlist"
                style={{ textDecoration: "none" }}
              >
                <DashboardStatCard
                  value={stats.chapterAttemptsCount}
                  label="Chapter Quizzes"
                  subText="Attempted"
                  percent={stats.chapterPercent}
                  icon="fa-solid fa-book-open"
                  theme="stat-pink"
                />
              </Link>
            </div>

            {/* STREAK */}
            <div className="col-12 col-md-4">
              <DashboardStatCard
                value={stats.streakCount}
                label="Streaks"
                subText="Continuous days activity"
                percent={stats.streakPercent}
                icon="fa-solid fa-bolt"
                theme="stat-orange"
              />
            </div>

          </div>

          {/* ===== CTA BUTTONS ===== */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">

              <div className="row justify-content-center text-center">

                {/* START TEST */}
                <div className="col-6 d-flex justify-content-center">
                  <Link
                    to="/subjects"
                    className="btn btn-primary fw-semibold w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      maxWidth: "320px",
                      fontSize: "18px",
                      textTransform: "uppercase",
                    }}
                  >
                    <i className="fa-solid fa-play"></i>
                    START TEST
                  </Link>
                </div>

                {/* ANALYSIS */}
                <div className="col-6 d-flex justify-content-center">
                  <Link
                    to="/analysis"
                    className="btn btn-primary fw-semibold w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      maxWidth: "320px",
                      fontSize: "18px",
                      textTransform: "uppercase",
                    }}
                  >
                    <i className="fa-solid fa-chart-line"></i>
                    VIEW ANALYSIS
                  </Link>
                </div>

              </div>
            </div>
          </div>

          {/* ===== LAST ATTEMPTS ===== */}
          <LastQuizAttempts />

        </div>
      </div>
    </>
  );
}